import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, RefreshCw } from 'lucide-react';
import { useOrgTree } from '../../hooks/useOrgTree';
import { normalizeOrgPerson } from '../../utils/orgTree';
import { OrgLeadCard, OrgMemberCard, OrgNodeCard, OrgPanel } from './OrgParts';
import './TeamStructure.css';

const pluralMembers = (count) => (count === 1 ? 'member' : 'members');

/** 'admin' | 'team_lead' | 'employee' from the user's raw role. */
const readRoleKey = (user) => {
    const role = String(user?.rawRole || user?.role || '').toLowerCase();
    if (role.includes('admin')) return 'admin';
    if (role.includes('lead') || role.includes('manager')) return 'team_lead';
    return 'employee';
};

/**
 * The reporting tree of one user, from GET /rfp/auth/users/{user_id}/org-tree.
 *
 *   admin      -> the admin(s) on top, then every team lead; selecting a lead shows that lead's team
 *   team lead  -> the lead on top, then their team members
 *   employee   -> the team lead they report to
 *
 * @param {{ user: object, userId: string, refreshKey?: number, emptyHint?: string }} props
 *        user: the normalized user being viewed; refreshKey: change it to load the tree again
 */
const TeamStructure = ({ user, userId, refreshKey = 0, emptyHint, canOpenProfiles = true }) => {
    const navigate = useNavigate();
    const { tree, status, error, reload } = useOrgTree(userId, refreshKey);
    const [selectedLeadId, setSelectedLeadId] = useState('');

    const roleKey = readRoleKey(user);
    // Opening someone's profile page is for admins; others only read the tree.
    const openProfile = canOpenProfiles ? (person) => navigate(`/users-roles/${person.id}`) : undefined;

    // The person at the top: the matching entry of the tree, or what the page already knows.
    const viewer = useMemo(() => {
        const fromTree = tree && [...tree.admins, ...tree.teamLeads].find((person) => person.id === String(userId));
        if (fromTree) return fromTree;
        return normalizeOrgPerson({
            id: userId,
            name: user?.name,
            role: user?.rawRole,
            avatar_url: user?.avatar_url,
            status: user?.status,
        });
    }, [tree, user, userId]);

    if (status === 'loading' && !tree) {
        return (
            <div className="org-state" role="status">
                <Loader2 size={22} className="org-spin" aria-hidden="true" />
                <span>Loading team structure...</span>
            </div>
        );
    }

    if (status === 'error' && !tree) {
        return (
            <div className="org-state org-state-error" role="alert">
                <span>{error}</span>
                <button type="button" className="org-retry" onClick={reload}>
                    <RefreshCw size={14} aria-hidden="true" />
                    Try again
                </button>
            </div>
        );
    }

    if (!tree) return null;

    /* Admin: all team leads; pick one to see their team */
    if (roleKey === 'admin') {
        const admins = tree.admins.length > 0 ? tree.admins : [viewer];
        const selectedLead = tree.teamLeads.find((lead) => lead.id === selectedLeadId) || null;

        return (
            <div className="org-tree">
                <div className="org-nodes">
                    {admins.map((admin) => (
                        <OrgNodeCard key={admin.id} person={admin} />
                    ))}
                </div>
                <span className="org-connector" aria-hidden="true" />

                <OrgPanel title="Team leads" count={tree.teamLeads.length} countLabel={tree.teamLeads.length === 1 ? 'team lead' : 'team leads'}>
                    {tree.teamLeads.length === 0 ? (
                        <p className="org-empty">There are no team leads yet.</p>
                    ) : (
                        <div className="org-grid">
                            {tree.teamLeads.map((lead) => (
                                <OrgLeadCard
                                    key={lead.id}
                                    person={lead}
                                    selected={lead.id === selectedLeadId}
                                    onSelect={(picked) => setSelectedLeadId(picked.id === selectedLeadId ? '' : picked.id)}
                                />
                            ))}
                        </div>
                    )}
                </OrgPanel>

                {tree.teamLeads.length > 0 && !selectedLead && (
                    <p className="org-hint">Select a team lead to see their team members.</p>
                )}

                {selectedLead && (
                    <>
                        <span className="org-connector" aria-hidden="true" />
                        <OrgPanel
                            title={`Team members of ${selectedLead.name}`}
                            count={selectedLead.members.length}
                            countLabel={pluralMembers(selectedLead.members.length)}
                        >
                            {selectedLead.members.length === 0 ? (
                                <p className="org-empty">{selectedLead.name} has no team members yet.</p>
                            ) : (
                                <div className="org-grid">
                                    {selectedLead.members.map((member) => (
                                        <OrgMemberCard key={member.id} person={member} onOpen={openProfile} />
                                    ))}
                                </div>
                            )}
                        </OrgPanel>
                    </>
                )}
            </div>
        );
    }

    /* Team lead: the lead on top, then their team */
    if (roleKey === 'team_lead') {
        const ownTeam = tree.teamLeads.find((lead) => lead.id === String(userId)) || tree.teamLeads[0] || null;
        const members = ownTeam ? ownTeam.members : [];

        return (
            <div className="org-tree">
                <OrgNodeCard person={ownTeam || viewer} />
                <span className="org-connector" aria-hidden="true" />

                <OrgPanel title="Team members" count={members.length} countLabel={pluralMembers(members.length)}>
                    {members.length === 0 ? (
                        <p className="org-empty">{emptyHint || 'No team members assigned yet.'}</p>
                    ) : (
                        <div className="org-grid">
                            {members.map((member) => (
                                <OrgMemberCard key={member.id} person={member} onOpen={openProfile} />
                            ))}
                        </div>
                    )}
                </OrgPanel>
            </div>
        );
    }

    /* Team member: the team lead they report to */
    const lead = tree.teamLeads[0] || null;
    return (
        <div className="org-tree">
            <span className="org-section-label">Reports to (team lead)</span>
            {lead ? (
                <OrgNodeCard person={lead} onClick={openProfile ? () => openProfile(lead) : undefined} />
            ) : (
                <p className="org-empty">Not assigned to a team lead yet.</p>
            )}
        </div>
    );
};

export default TeamStructure;
